import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Collapse, TablePagination, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Payment as PaymentIcon,
  Assessment as AssessmentIcon, AccountBalance as AccountIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  AttachMoney as MoneyIcon, TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon, CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const MONTHS = [
  { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => currentYear - 2 + i);

/** Normaliza lista de profissionais da API (by_professional vs professionals vs array direto). */
function parseConsolidationProfessionals(payload: any): any[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.by_professional)) return payload.by_professional;
  if (Array.isArray(payload.professionals)) return payload.professionals;
  return [];
}

const Repasses: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [tab, setTab] = useState(0);

  // --- Consolidação state ---
  const [refMonth, setRefMonth] = useState(new Date().getMonth() + 1);
  const [refYear, setRefYear] = useState(currentYear);
  const [consolidation, setConsolidation] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loadingConsolidation, setLoadingConsolidation] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [payDialog, setPayDialog] = useState(false);
  const [payMethod, setPayMethod] = useState('pix');
  const [payRef, setPayRef] = useState('');
  const [paying, setPaying] = useState(false);
  const [filterProfessionalId, setFilterProfessionalId] = useState('');

  // --- Repasses Consolidados state ---
  const [consolidated, setConsolidated] = useState<any[]>([]);
  const [consolidatedTotal, setConsolidatedTotal] = useState(0);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);
  const [consYear, setConsYear] = useState(currentYear);
  const [consProfessionalId, setConsProfessionalId] = useState('');
  const [consPage, setConsPage] = useState(0);
  const [consLimit, setConsLimit] = useState(25);

  // --- Shared ---
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true } })
      .then((res) => setProfessionals(res.data?.data || []))
      .catch(() => setProfessionals([]));
  }, []);

  // --- Consolidação ---
  const loadConsolidation = useCallback(async () => {
    try {
      setLoadingConsolidation(true);
      const params: any = { reference_month: refMonth, reference_year: refYear };
      if (filterProfessionalId) params.professional_id = filterProfessionalId;
      const { data } = await axios.get(getApiUrl('/financial-module/repasses/consolidation'), { params });
      const payload = data?.data;
      setConsolidation(parseConsolidationProfessionals(payload));
      setTotals(payload?.totals || data?.totals || {});
    } catch (e) {
      console.error(e);
      setConsolidation([]);
      setTotals({});
      setSnackbar({ open: true, message: 'Erro ao carregar consolidação', severity: 'error' });
    } finally {
      setLoadingConsolidation(false);
    }
  }, [refMonth, refYear, filterProfessionalId]);

  useEffect(() => {
    if (tab === 0) loadConsolidation();
  }, [tab, loadConsolidation]);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSelectItem = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handlePay = async () => {
    if (selectedIds.length === 0) return;
    try {
      setPaying(true);
      await axios.post(getApiUrl('/financial-module/repasses/pay'), {
        ids: selectedIds,
        payment_method: payMethod,
        payment_reference: payRef,
      });
      setSnackbar({ open: true, message: 'Pagamento registrado com sucesso!', severity: 'success' });
      setPayDialog(false);
      setSelectedIds([]);
      setPayMethod('pix');
      setPayRef('');
      loadConsolidation();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao registrar pagamento', severity: 'error' });
    } finally {
      setPaying(false);
    }
  };

  // --- Repasses Consolidados ---
  const loadConsolidated = useCallback(async () => {
    try {
      setLoadingConsolidated(true);
      const params: any = { year: consYear, page: consPage + 1, limit: consLimit };
      if (consProfessionalId) params.professional_id = consProfessionalId;
      const { data } = await axios.get(getApiUrl('/financial-module/repasses/consolidated'), { params });
      const rows = data?.data;
      setConsolidated(Array.isArray(rows) ? rows : []);
      setConsolidatedTotal(data.pagination?.total ?? data.total ?? 0);
    } catch (e) {
      console.error(e);
      setConsolidated([]);
      setConsolidatedTotal(0);
      setSnackbar({ open: true, message: 'Erro ao carregar repasses consolidados', severity: 'error' });
    } finally {
      setLoadingConsolidated(false);
    }
  }, [consYear, consProfessionalId, consPage, consLimit]);

  useEffect(() => {
    if (tab === 1) loadConsolidated();
  }, [tab, loadConsolidated]);

  const totalsCards = [
    { label: 'Total Bruto', value: totals.total_gross || 0, color: '#3b82f6', icon: <MoneyIcon /> },
    { label: 'Deduções', value: totals.total_deductions || 0, color: '#ef4444', icon: <TrendingDownIcon /> },
    { label: 'Total Comissões', value: totals.total_commissions || 0, color: '#14B8A6', icon: <ReceiptIcon /> },
    { label: 'Pago', value: totals.total_paid || 0, color: '#22c55e', icon: <CheckCircleIcon /> },
    { label: 'Pendente', value: totals.total_pending || 0, color: '#f59e0b', icon: <PendingIcon /> },
  ];

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Repasses</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Consolidação e histórico de repasses aos profissionais</Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#14B8A6' },
          '& .MuiTabs-indicator': { backgroundColor: '#14B8A6' },
        }}
      >
        <Tab icon={<AssessmentIcon />} iconPosition="start" label="Consolidação" />
        <Tab icon={<AccountIcon />} iconPosition="start" label="Repasses Consolidados" />
      </Tabs>

      {/* ========== TAB 0 - CONSOLIDAÇÃO ========== */}
      {tab === 0 && (
        <>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mês</InputLabel>
              <Select value={refMonth} label="Mês" onChange={(e) => setRefMonth(Number(e.target.value))}>
                {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Ano</InputLabel>
              <Select value={refYear} label="Ano" onChange={(e) => setRefYear(Number(e.target.value))}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Profissional</InputLabel>
              <Select value={filterProfessionalId} label="Profissional" onChange={(e) => setFilterProfessionalId(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Atualizar">
              <IconButton onClick={loadConsolidation} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
            </Tooltip>
            {selectedIds.length > 0 && (
              <Button
                variant="contained"
                startIcon={<PaymentIcon />}
                onClick={() => setPayDialog(true)}
                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, ml: 'auto' }}
              >
                Pagar ({selectedIds.length})
              </Button>
            )}
          </Box>

          {/* Totals Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {totalsCards.map((card, idx) => (
              <Grid item xs={12} sm={6} md={2.4} key={idx}>
                <Card sx={{ borderRadius: 3, bgcolor: 'white', border: `1px solid ${card.color}22` }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{card.label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                        {formatCurrency(card.value)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Consolidation Table */}
          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Bruto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Deduções</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Comissão</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Pago</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Pendente</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingConsolidation ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={36} sx={{ color: '#14B8A6' }} />
                      </TableCell>
                    </TableRow>
                  ) : consolidation.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                        Nenhum dado de consolidação para o período
                      </TableCell>
                    </TableRow>
                  ) : consolidation.map((prof) => (
                    <React.Fragment key={prof.professional_id || prof.id}>
                      <TableRow hover sx={{ '& > *': { borderBottom: expandedRows[prof.professional_id] ? 'none' : undefined } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleRow(prof.professional_id)}>
                            {expandedRows[prof.professional_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{prof.professional_name || '-'}</TableCell>
                        <TableCell>{prof.professional_specialty || prof.specialty || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(prof.total_gross ?? prof.gross_total)}</TableCell>
                        <TableCell align="right" sx={{ color: '#ef4444' }}>{formatCurrency(prof.total_deductions ?? prof.deductions_total)}</TableCell>
                        <TableCell align="right" sx={{ color: '#14B8A6', fontWeight: 600 }}>{formatCurrency(prof.total_commissions ?? prof.commission_total)}</TableCell>
                        <TableCell align="right" sx={{ color: '#22c55e' }}>{formatCurrency(prof.total_paid ?? prof.paid_total)}</TableCell>
                        <TableCell align="right" sx={{ color: '#f59e0b', fontWeight: 600 }}>{formatCurrency(prof.total_pending ?? prof.pending_total)}</TableCell>
                        <TableCell align="center">
                          <Chip label={prof.count ?? prof.items_count ?? 0} size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                        </TableCell>
                      </TableRow>

                      {/* Expanded items */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, px: 0 }}>
                          <Collapse in={expandedRows[prof.professional_id]} timeout="auto" unmountOnExit>
                            <Box sx={{ mx: 4, my: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Sel.</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Paciente</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Procedimento</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Bruto</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Dedução</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Comissão</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Ação</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(prof.items || []).map((item: any) => (
                                    <TableRow key={item.id} hover>
                                      <TableCell>
                                        {item.payment_status === 'pending' && (
                                          <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => toggleSelectItem(item.id)}
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>{item.patient_name || '-'}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>{item.procedure_name || '-'}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(item.gross_amount)}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{formatCurrency(item.deductions ?? item.deduction_amount)}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#14B8A6' }}>{formatCurrency(item.commission_amount)}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={item.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                                          size="small"
                                          sx={{
                                            bgcolor: item.payment_status === 'paid' ? '#dcfce7' : '#fef3cd',
                                            color: item.payment_status === 'paid' ? '#22c55e' : '#f59e0b',
                                            fontWeight: 600, fontSize: '0.7rem',
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {item.payment_status === 'pending' && (
                                          <Tooltip title="Pagar este item">
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                setSelectedIds([item.id]);
                                                setPayDialog(true);
                                              }}
                                              sx={{ color: '#22c55e' }}
                                            >
                                              <PaymentIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {(!prof.items || prof.items.length === 0) && (
                                    <TableRow>
                                      <TableCell colSpan={8} align="center" sx={{ color: '#94a3b8', py: 2 }}>
                                        Nenhum item detalhado
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* ========== TAB 1 - REPASSES CONSOLIDADOS ========== */}
      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Ano</InputLabel>
              <Select value={consYear} label="Ano" onChange={(e) => { setConsYear(Number(e.target.value)); setConsPage(0); }}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Profissional</InputLabel>
              <Select value={consProfessionalId} label="Profissional" onChange={(e) => { setConsProfessionalId(e.target.value); setConsPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Atualizar">
              <IconButton onClick={loadConsolidated} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>

          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mês/Ano</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Bruto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Deduções</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Comissões</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingConsolidated ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={36} sx={{ color: '#14B8A6' }} />
                      </TableCell>
                    </TableRow>
                  ) : consolidated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                        Nenhum repasse consolidado encontrado
                      </TableCell>
                    </TableRow>
                  ) : consolidated.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.professional_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${String(row.reference_month || row.month).padStart(2, '0')}/${row.reference_year || row.year}`}
                          size="small"
                          sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.gross_total || row.total_gross)}</TableCell>
                      <TableCell align="right" sx={{ color: '#ef4444' }}>{formatCurrency(row.deductions_total || row.total_deductions)}</TableCell>
                      <TableCell align="right" sx={{ color: '#14B8A6', fontWeight: 600 }}>{formatCurrency(row.commission_total || row.total_commissions)}</TableCell>
                      <TableCell align="center">
                        <Chip label={row.items_count || row.count || 0} size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {consolidated.length > 0 && (
              <TablePagination
                component="div"
                count={consolidatedTotal}
                page={consPage}
                onPageChange={(_, p) => setConsPage(p)}
                rowsPerPage={consLimit}
                onRowsPerPageChange={(e) => { setConsLimit(parseInt(e.target.value, 10)); setConsPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Linhas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
              />
            )}
          </Card>
        </>
      )}

      {/* ========== PAY DIALOG ========== */}
      <Dialog fullScreen={isMobile} scroll="paper" open={payDialog} onClose={() => setPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Registrar Pagamento</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2, color: '#64748b' }}>
            {selectedIds.length} item(ns) selecionado(s) para pagamento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select value={payMethod} label="Forma de Pagamento" onChange={(e) => setPayMethod(e.target.value)}>
                  <MenuItem value="pix">PIX</MenuItem>
                  <MenuItem value="bank_transfer">Transferência Bancária</MenuItem>
                  <MenuItem value="cash">Dinheiro</MenuItem>
                  <MenuItem value="check">Cheque</MenuItem>
                  <MenuItem value="deposit">Depósito</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Referência do Pagamento"
                placeholder="Ex: Comprovante, Nº transação"
                value={payRef}
                onChange={(e) => setPayRef(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handlePay}
            disabled={paying}
            startIcon={paying ? <CircularProgress size={18} /> : <PaymentIcon />}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
          >
            Confirmar Pagamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== SNACKBAR ========== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Repasses;
